
import { supabase } from '../supabase';
import { User, UserRole, ClassGroup, ClassStats, Order, StudentStats, UserStatus, DEFAULT_PAYMENT_OPTIONS, PaymentOptions } from '../../types';
import { BonusTotals, bonusTotals, paymentStatusCounts } from '../bonus';
import { mapUser, DEFAULT_VALIDITY_DAYS } from '../auth_utils';
import { fetchAll } from './fetchAll';

export const getAllUsers = async (currentUser: User, includeDeleted: boolean = false): Promise<User[]> => {
    // SECURITY / SIMULATION: Students should never see the user list.
    if (currentUser.role === UserRole.STUDENT) {
        return [];
    }

    const build = () => {
        let query = supabase.from('profiles').select('*');

        if (!includeDeleted) {
            query = query.is('deleted_at', null);
        }

        if (currentUser.role === UserRole.TEACHER) {
            // Fix: Explicitly check for valid UUID string to prevent "invalid input syntax for type uuid: 'null'"
            const classId = currentUser.assignedClassId;
            const isValidClassId = classId && classId !== 'null' && classId !== 'undefined';

            if (isValidClassId) {
                query = query.or(`class_id.eq.${classId},id.eq.${currentUser.id}`);
            } else {
                query = query.eq('id', currentUser.id);
            }
        }
        return query;
    };

    try {
        // Fler än 1 000 användare hämtas sida för sida.
        const data = await fetchAll((from, to) => build().order('id').range(from, to));
        return data.map(mapUser);
    } catch (error) {
        console.error("Error fetching users:", error);
        return [];
    }
};

export const getClassTeachers = async (classId: string): Promise<User[]> => {
    // Hämtar ALLA lärare som är ansvariga för en specifik klass
    const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'TEACHER')
        .eq('assigned_class_id', classId);
    
    if (error) {
        console.error("Error fetching class teachers:", error);
        return [];
    }
    return (data || []).map(mapUser);
};

export const getUserBySlug = async (slug: string): Promise<User | null> => {
    const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('unique_slug', slug)
        .is('deleted_at', null)
        .maybeSingle();
    
    if (error || !data) return null;
    return mapUser(data);
};

export const getStoreData = async (slug: string): Promise<{ student: User, classInfo: ClassGroup } | null> => {
    const { data, error } = await supabase.rpc('get_public_store_data', { slug_input: slug });
    
    if (error || !data) return null;

    const student: User = {
        id: data.student.id,
        name: data.student.name,
        role: UserRole.STUDENT,
        status: 'ACTIVE',
        validUntil: 0,
        email: '',
        classId: data.student.classId,
        // Koden butiken öppnades med. Behövs för statistiken per produkt
        // (record_product_event), som annars aldrig skickades.
        uniqueSlug: slug,
        avatarUrl: data.student.avatarUrl,
        // Bara när gruppen har Swish till säljaren.
        swishNumber: data.student.swishNumber || undefined
    };

    const classInfo: ClassGroup = {
        id: data.student.classId || 'unknown',
        name: data.classInfo.name || '', 
        salesGoal: 0,
        teacherId: '',
        campaignEnd: data.classInfo.campaignEnd ? new Date(data.classInfo.campaignEnd).getTime() : undefined,
        paymentOptions: { ...DEFAULT_PAYMENT_OPTIONS, ...(data.classInfo.paymentOptions || {}) }
    };

    return { student, classInfo };
};

export const updateUserStatus = async (userId: string, status: UserStatus) => {
    const { error } = await supabase.from('profiles').update({
        status: status
    }).eq('id', userId);
    
    if (error) throw error;
};

export const softDeleteUser = async (userId: string) => {
    const { error } = await supabase.from('profiles').update({
        deleted_at: new Date().toISOString(), 
        status: 'DEACTIVATED'
    }).eq('id', userId);
    if (error) throw error;
};

export const restoreUser = async (userId: string) => {
    const { error } = await supabase.from('profiles').update({
        deleted_at: null,
        status: 'ACTIVE'
    }).eq('id', userId);
    if (error) throw error;
};

export const assignTeacherToClass = async (teacherId: string, classId: string) => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setDate(oneYearFromNow.getDate() + DEFAULT_VALIDITY_DAYS);

    const { error } = await supabase.from('profiles').update({
        assigned_class_id: classId,
        valid_until: oneYearFromNow.toISOString()
    }).eq('id', teacherId);
    
    if (error) throw error;
};

export const getClasses = async (user?: User, includeDeleted: boolean = false): Promise<ClassGroup[]> => {
    let query = supabase.from('classes').select('*');

    if (!includeDeleted) {
        query = query.is('deleted_at', null);
    }

    if (user?.role === UserRole.TEACHER) {
        // Hämta klasser där läraren är ägare ELLER tilldelad
        // Detta täcker både nya (teacher_id) och gamla (via profile) kopplingar
        if (user.assignedClassId) {
            query = query.or(`id.eq.${user.assignedClassId},teacher_id.eq.${user.id}`);
        } else {
            query = query.eq('teacher_id', user.id);
        }
    }

    const { data, error } = await query;
    if (error) {
        console.error("Error fetching classes:", error);
        return [];
    }
    
    return data.map(row => ({
        id: row.id,
        name: row.name,
        salesGoal: row.sales_goal || 10000,
        deliveryAddress: row.delivery_address,
        campaignStart: row.campaign_start ? new Date(row.campaign_start).getTime() : undefined,
        campaignEnd: row.campaign_end ? new Date(row.campaign_end).getTime() : undefined,
        teacherId: row.teacher_id || '',
        deletedAt: row.deleted_at ? new Date(row.deleted_at).getTime() : undefined,
        renewalStatus: row.renewal_status,
        requestedName: row.requested_name,
        payoutAccount: row.payout_account || undefined,
        paymentOptions: {
            stripe: !!row.payment_stripe,
            homeDelivery: !!row.delivery_home,
            sellerSwish: !!row.payment_seller_swish,
            sellerDirect: row.payment_seller_direct !== false,
            stripeHomeOnly: !!row.payment_stripe_home_only,
        }
    }));
};

export const softDeleteClass = async (classId: string) => {
    const { error } = await supabase.from('classes').update({
        deleted_at: new Date().toISOString()
    }).eq('id', classId);
    if (error) throw error;
};

export const restoreClass = async (classId: string) => {
    const { error } = await supabase.from('classes').update({
        deleted_at: null
    }).eq('id', classId);
    if (error) throw error;
};

export const createClass = async (teacherId: string | null, name: string, salesGoal: number, start: number | null, end: number | null, address?: string) => {
    // Om teacherId inte skickas med (admin skapar utan att tilldela), använd inloggad användare som ägare
    const currentUser = (await supabase.auth.getUser()).data.user;
    const ownerId = teacherId || currentUser?.id;

    const { data, error } = await supabase.from('classes').insert([{
        name,
        sales_goal: salesGoal,
        delivery_address: address,
        campaign_start: start ? new Date(start).toISOString() : null,
        campaign_end: end ? new Date(end).toISOString() : null,
        teacher_id: ownerId // Sätter ägaren direkt!
    }]).select().single();
    
    if (error) throw error;

    // Trigger i databasen hanterar uppdatering av 'assigned_class_id' på profilen,
    // men för säkerhets skull (om admin skapar åt en annan lärare) kör vi en assign också.
    if (teacherId && teacherId !== currentUser?.id) {
        await assignTeacherToClass(teacherId, data.id);
    }
    
    return data;
};

export const updateClassDetails = async (id: string, name: string, salesGoal: number, start: number | null, end: number | null, address: string) => {
    const { error } = await supabase.from('classes').update({
        name,
        sales_goal: salesGoal,
        delivery_address: address,
        campaign_start: start ? new Date(start).toISOString() : null, 
        campaign_end: end ? new Date(end).toISOString() : null
    }).eq('id', id);
    if (error) throw error;
};

// --- RENEWAL LOGIC ---

export const requestClassRenewal = async (classId: string, requestedName: string) => {
    const { error } = await supabase.from('classes').update({
        renewal_status: 'REQUESTED',
        requested_name: requestedName
    }).eq('id', classId);
    if (error) throw error;
};

export const rejectClassRenewal = async (classId: string) => {
    const { error } = await supabase.from('classes').update({
        renewal_status: 'NONE',
        requested_name: null
    }).eq('id', classId);
    if (error) throw error;
};

export const approveClassRenewal = async (oldClassId: string, newName: string) => {
    const { data: oldClass } = await supabase.from('classes').select('*').eq('id', oldClassId).single();
    if (!oldClass) throw new Error("Class not found");

    const { data: newClass, error: createError } = await supabase.from('classes').insert([{
        name: newName,
        sales_goal: oldClass.sales_goal,
        delivery_address: oldClass.delivery_address,
        campaign_start: null,
        campaign_end: null,
        teacher_id: oldClass.teacher_id // Behåll samma lärare
    }]).select().single();
    
    if (createError) throw createError;

    const { error: moveStudentsError } = await supabase
        .from('profiles')
        .update({ class_id: newClass.id })
        .eq('class_id', oldClassId);
    if (moveStudentsError) throw moveStudentsError;

    const { error: moveTeachersError } = await supabase
        .from('profiles')
        .update({ assigned_class_id: newClass.id })
        .eq('assigned_class_id', oldClassId);
    if (moveTeachersError) throw moveTeachersError;

    const archivedName = `${oldClass.name} (Arkiverad ${new Date().toLocaleDateString('sv-SE')})`;
    await supabase.from('classes').update({ 
        deleted_at: new Date().toISOString(),
        renewal_status: 'PROCESSED',
        name: archivedName
    }).eq('id', oldClassId);
};

const mapClassInfo = (cls: any): ClassGroup => ({
    id: cls.id,
    name: cls.name,
    salesGoal: cls.sales_goal || 10000,
    teacherId: cls.teacher_id,
    renewalStatus: cls.renewal_status,
    requestedName: cls.requested_name,
    campaignStart: cls.campaign_start ? new Date(cls.campaign_start).getTime() : undefined,
    campaignEnd: cls.campaign_end ? new Date(cls.campaign_end).getTime() : undefined,
} as ClassGroup);

const itemsIn = (orders: any[]) => orders.reduce((sum, o) => sum + (o.items || []).reduce((s: number, i: any) => s + Number(i.quantity || 0), 0), 0);

/** Gruppens bonusläge ur databasen (säljare: egen grupp, ansvarig: sin grupp, admin: valfri). */
const parseBonus = (b: any): BonusTotals => ({
    regularSales: Number(b?.regularSales || 0),
    regularItems: Number(b?.regularItems || 0),
    fixedSales: Number(b?.fixedSales || 0),
    fixedItems: Number(b?.fixedItems || 0),
    fixedCommission: Number(b?.fixedCommission || 0),
    itemsSold: Number(b?.itemsSold || 0),
});

/**
 * Statistik per grupp för översikterna. Säljare får gruppens bonusläge och
 * antal sålda produkter per säljare, aldrig kronor per säljare. Ansvarig och
 * admin räknar ur gruppens ordrar (de får läsa dem).
 */
export const getClassStats = async (user: User): Promise<ClassStats[]> => {
    if (user.role === UserRole.STUDENT) {
        if (!user.classId) return [];

        const { data: classData } = await supabase.from('classes').select('*').eq('id', user.classId).single();
        if (!classData) return [];

        const [{ data: leaderboard }, { data: bonusRow }] = await Promise.all([
            supabase.rpc('get_class_leaderboard_items_secure'),
            supabase.rpc('get_class_bonus_secure', {}),
        ]);
        let students: StudentStats[] = (leaderboard || []).map((row: any) => ({
            id: row.student_id,
            name: row.student_name,
            orderCount: Number(row.order_count),
            itemsSold: Number(row.items_sold),
        }));
        let bonus = parseBonus(bonusRow);
        let sellerCount = Number(bonusRow?.sellerCount || students.length);

        // Admin som tittar som säljare: RPC:erna utgår från inloggad användare, så räkna själv.
        if (students.length === 0) {
            try {
                const { data: members } = await supabase.from('profiles').select('id, name').eq('class_id', user.classId).is('deleted_at', null);
                const { data: orders } = await supabase.from('orders')
                    .select('student_id, items, payment_status, deleted_at')
                    .eq('class_id', user.classId)
                    .is('deleted_at', null);
                const counted = (orders || []).filter(o => paymentStatusCounts(o.payment_status));
                students = (members || []).map(m => {
                    const mine = counted.filter(o => o.student_id === m.id);
                    return { id: m.id, name: m.name, orderCount: mine.length, itemsSold: itemsIn(mine) };
                });
                bonus = bonusTotals(counted.map(o => ({ items: o.items || [] }) as unknown as Order));
                sellerCount = students.length;
            } catch (e) {
                console.warn("Manual stats fetch failed during simulation", e);
            }
        }

        return [{
            classInfo: mapClassInfo(classData),
            students,
            totalSales: bonus.regularSales + bonus.fixedSales,
            bonus,
            sellerCount,
        }];
    }

    let classes: any[] = [];

    try {
        if (user.role === UserRole.ADMIN) {
            const { data } = await supabase.from('classes').select('*').is('deleted_at', null);
            classes = data || [];
        } else if (user.role === UserRole.TEACHER) {
            // Hämta klasser via teacher_id eller assigned_class_id
            const query = supabase.from('classes').select('*').is('deleted_at', null);
            if (user.assignedClassId) {
                query.or(`id.eq.${user.assignedClassId},teacher_id.eq.${user.id}`);
            } else {
                query.eq('teacher_id', user.id);
            }
            const { data } = await query;
            classes = data || [];
        }
    } catch(e) { console.error(e); return []; }

    const classIds = classes.map(c => c.id);
    if (classIds.length === 0) return [];

    // Hämtas sida för sida: med många grupper blir det fler än 1 000 rader.
    let safeUsers: any[] = [];
    let orders: any[] = [];
    try {
        [safeUsers, orders] = await Promise.all([
            fetchAll((from, to) => supabase.from('profiles').select('*').in('class_id', classIds).order('id').range(from, to)),
            // Säljperioden är gruppens livstid: bara ordrar som hör till gruppen räknas.
            fetchAll((from, to) => supabase.from('orders')
                .select('id, student_id, class_id, total_amount, status, items, payment_status')
                .in('class_id', classIds)
                .is('deleted_at', null)
                .order('id')
                .range(from, to)),
        ]);
    } catch (e) { console.error(e); return []; }
    const safeOrders = orders.filter(o => paymentStatusCounts(o.payment_status));

    return classes.map(cls => {
        const classUsers = safeUsers.filter(u => u.class_id === cls.id);
        const classOrders = safeOrders.filter(o => o.class_id === cls.id);

        const students: StudentStats[] = classUsers.map(u => {
            const userOrders = classOrders.filter(o => o.student_id === u.id);
            return {
                id: u.id,
                name: u.name,
                isArchived: !!u.deleted_at,
                orderCount: userOrders.length,
                itemsSold: itemsIn(userOrders),
                totalSales: userOrders.reduce((sum: number, o: any) => sum + Number(o.total_amount), 0)
            };
        });
        const bonus = bonusTotals(classOrders.map(o => ({ items: o.items || [] }) as unknown as Order));

        return {
            classInfo: mapClassInfo(cls),
            students,
            totalSales: classOrders.reduce((sum: number, o: any) => sum + Number(o.total_amount), 0),
            bonus,
            // Den ansvariga säljer också (har en säljlänk), som i get_class_bonus_secure.
            sellerCount: classUsers.filter(u => (u.role === 'STUDENT' || u.role === 'TEACHER') && !u.deleted_at).length,
        };
    });
};

/** Föreningens konto för utbetalningar. Fylls i av den ansvariga. */
export const updateClassPayoutAccount = async (classId: string, payoutAccount: string) => {
    const value = payoutAccount.trim().slice(0, 100);
    const { error } = await supabase.from('classes').update({ payout_account: value || null }).eq('id', classId);
    if (error) throw error;
};

/** Admin: gruppens betalsätt. Databasen kräver minst ett betalsätt och att hemleverans har betalning i kassan. */
export const updateClassPaymentOptions = async (classId: string, options: PaymentOptions) => {
    const { error } = await supabase.from('classes').update({
        payment_stripe: options.stripe,
        delivery_home: options.stripe && options.homeDelivery,
        payment_seller_swish: options.sellerSwish,
        payment_seller_direct: options.sellerDirect,
        payment_stripe_home_only: options.stripeHomeOnly,
    }).eq('id', classId);
    if (error) throw error;
};

/** Säljarens eget Swishnummer: tio siffror (07… eller 123…), tomt tar bort det. */
export const normalizeSwishNumber = (raw: string): string | null | 'invalid' => {
    const digits = raw.replace(/[^0-9+]/g, '').replace(/^\+46/, '0').replace(/^0046/, '0');
    if (!digits) return null;
    return /^(07\d{8}|123\d{7})$/.test(digits) ? digits : 'invalid';
};

export const updateMySwishNumber = async (userId: string, raw: string) => {
    const value = normalizeSwishNumber(raw);
    if (value === 'invalid') throw new Error('Ange ett mobilnummer (07…) eller Swishnummer (123…) med tio siffror.');
    const { error } = await supabase.from('profiles').update({ swish_number: value }).eq('id', userId);
    if (error) throw error;
    return value;
};

/** "070-123 45 67" eller "123 456 78 90" för visning. */
export const formatSwishNumber = (n: string): string =>
    n.startsWith('123') ? `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6, 8)} ${n.slice(8)}` : `${n.slice(0, 3)}-${n.slice(3, 6)} ${n.slice(6, 8)} ${n.slice(8)}`;
